// The one translation from a refused or failed API call to the sentence the
// user reads. The server answers with a short code (`invalid_reference`) or a
// sentence of its own, and sometimes a `detail` with the reason. The sentence
// for a code stands on its own: a response that carries no detail (9.22 removes
// it wherever it could name a backend, a path or an internal condition) still
// reads as plain words.

const CODE_SENTENCES: Record<string, string> = {
  sign_in_required: 'Sign in to do this.',
  session_required: 'Your session has ended. Sign in again.',
  session_superseded: 'You signed in somewhere else, so this session ended. Sign in again.',
  module_not_entitled: "This site's subscription does not include this feature.",
  rate_limited: 'Too many requests in a short time. Wait a moment and try again.',
  limit_exceeded: 'This went over a limit set for this site, so it was stopped.',
  result_too_large: 'The result is too large to show. Narrow the request and try again.',
  service_account_quota_exceeded:
    'This site already has as many service accounts as its subscription allows.',
  entity_ceiling_exceeded:
    'This would take the site past the number of things its subscription allows.',
  not_found: 'That was not found. It may have been removed; refresh the page.',
  statement_gone: 'That fact is no longer there: it was removed elsewhere, so nothing was changed.',
  invalid_reference: 'A term in this change is not one the database can store.',
  invalid_query: 'The query could not be read.',
  conflict: 'This conflicts with what is already stored.',
  refused: 'The database refused this change.',
  backend_rejected: 'The database refused this change.',
  read_only_source: 'This comes from a read-only source, so it cannot be changed here.',
  rules_unsupported: 'Rules are not available on this database.',
  imported_binding: 'This prefix came from loaded data and cannot be deleted here.',
  usage_not_checked: 'Check which prefixes are used first. Nothing was removed.',
  data_changed: 'The data may have changed since the check. Check again. Nothing was removed.',
  terms_acceptance_failed: 'Your acceptance of the terms was not recorded. Try again.',
  invalid_terms_version:
    'The terms changed after this page was opened. Reload the page and accept the current terms.',
  state_layer_unavailable:
    "The site's settings store is not available right now. Try again in a minute.",
  substrate_unreachable: 'The database could not be reached. Try again in a minute.',
  substrate_timeout: 'The database took too long to answer. Try again, or narrow the request.',
  substrate_failed: 'The database could not finish this. Try again; if it repeats, report the issue.',
  db_error: 'The site could not save or read this. Try again; if it repeats, report the issue.',
  store_error: 'The site could not save or read this. Try again; if it repeats, report the issue.',
  registry_unreadable: 'The list of prefixes could not be read. Nothing was removed.',
  'AI provider unreachable': 'The AI provider could not be reached. Try again in a minute.',
  'AI provider authentication failed':
    "The AI provider refused this site's key. An administrator can check it under Admin, AI provider.",
  'AI provider rate-limited': 'The AI provider is limiting requests. Wait a moment and ask again.',
  'AI request shape error': 'The AI provider did not accept the request.',
  'AI provider returned malformed response': "The AI provider's answer could not be read.",
  'AI model error': 'The AI model returned an error.',
  'network error': 'The site could not be reached. Check the connection and try again.',
  'internal AI error': 'The assistant hit an error and did not finish.',
};

// Codes whose detail, when the server sends one, is internal text rather than
// a reason the user can act on.
const INTERNAL_CODES = new Set([
  'db_error',
  'store_error',
  'substrate_failed',
  'substrate_unreachable',
  'substrate_timeout',
  'state_layer_unavailable',
  'registry_unreadable',
]);

// Codes whose detail is already the whole plain sentence; the code's own
// sentence is used only when the detail is absent.
const DETAIL_IS_THE_SENTENCE = new Set(['imported_binding', 'usage_not_checked', 'data_changed']);

function statusSentence(status: number): string {
  if (status === 0) return 'The site could not be reached. Check the connection and try again.';
  if (status === 400) return 'The request was not accepted.';
  if (status === 401) return 'Sign in again to continue.';
  if (status === 403) return 'Your role does not allow this.';
  if (status === 404) return CODE_SENTENCES.not_found as string;
  if (status === 409) return CODE_SENTENCES.conflict as string;
  if (status === 413) return 'This is larger than the site accepts.';
  if (status === 422) return 'The request was not accepted.';
  if (status === 429) return CODE_SENTENCES.rate_limited as string;
  if (status === 502 || status === 503 || status === 504) {
    return 'The site is not answering right now; it may be restarting. Try again in a minute.';
  }
  if (status >= 500) {
    return 'The site hit an error and did not finish this. Try again; if it repeats, report the issue.';
  }
  return 'The request did not succeed.';
}

const CODE_SHAPE = /^[a-z][a-z0-9]*(_[a-z0-9]+)*$/;

function asSentence(text: string): string {
  const t = text.trim();
  if (!t) return t;
  const capital = t.charAt(0).toUpperCase() + t.slice(1);
  return /[.!?]$/.test(capital) ? capital : `${capital}.`;
}

/** The plain sentence for a failed call: the code's meaning, then the server's
 *  reason when it is one the user can act on. Never depends on `detail`. */
export function describeApiError(
  status: number,
  error: string | undefined,
  detail?: string | null,
): string {
  const code = (error ?? '').trim();
  const known = CODE_SENTENCES[code];
  const internal = INTERNAL_CODES.has(code) || status === 0;
  const reason = !internal && detail && detail.trim() ? asSentence(detail) : undefined;

  if (known) {
    if (reason && DETAIL_IS_THE_SENTENCE.has(code)) return reason;
    if (reason && !reason.toLowerCase().includes(known.toLowerCase().replace(/\.$/, ''))) {
      return `${known} ${reason}`;
    }
    return reason ?? known;
  }
  const lead = code && !CODE_SHAPE.test(code) && !/^HTTP \d+$/.test(code)
    ? asSentence(code)
    : statusSentence(status);
  if (reason && reason.toLowerCase() !== lead.toLowerCase()) return `${lead} ${reason}`;
  return lead;
}
